import React, { useEffect, useMemo, useState } from "react";
import Collapse from "react-bootstrap/Collapse";
import { useTranslation } from "react-i18next";

import {
  groupTicketsForEntryQrDisplay,
  normalizeTicketQrImgSrc,
  pickTicketQrSrc,
} from "./orderTicketsShared";

function guestLabel(t, guestIndex, guestCount) {
  return t("MY_TICKETS_GUEST_N_OF_M", { n: guestIndex, total: guestCount });
}

function QrImage({ ticket, t }) {
  const qr = pickTicketQrSrc(ticket);
  if (!qr) {
    return (
      <p className="small text-center my-tickets-confirm mb-0">
        {t("MY_TICKETS_NO_QR")}
      </p>
    );
  }
  return (
    <div className="my-tickets-qr-frame mx-auto">
      <img
        src={normalizeTicketQrImgSrc(qr)}
        alt=""
        className="my-tickets-qr-img"
      />
    </div>
  );
}

/**
 * Category → ticket unit → guest entry QR accordion (club mini-site style).
 */
const EntryQrAccordion = ({ tickets }) => {
  const { t } = useTranslation();
  const groups = useMemo(
    () => groupTicketsForEntryQrDisplay(tickets),
    [tickets]
  );

  const totalGuests = useMemo(
    () => groups.reduce((sum, g) => sum + g.totalPasses, 0),
    [groups]
  );

  const [expandedCats, setExpandedCats] = useState(() => new Set());
  const [expandedUnits, setExpandedUnits] = useState(() => new Set());
  const [expandedGuests, setExpandedGuests] = useState(() => new Set());

  useEffect(() => {
    if (!groups.length) return;
    setExpandedCats(new Set([groups[0].categoryKey]));
    const first = groups[0];
    if (first.units.length === 1) {
      setExpandedUnits(new Set([`${first.categoryKey}|${first.units[0].unitKey}`]));
    } else {
      setExpandedUnits(new Set());
    }
    setExpandedGuests(new Set());
  }, [groups]);

  const toggleCat = (key) => {
    setExpandedCats((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const toggleUnit = (catKey, unitKey) => {
    const id = `${catKey}|${unitKey}`;
    setExpandedUnits((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleGuest = (flatKey) => {
    setExpandedGuests((prev) => {
      const next = new Set(prev);
      if (next.has(flatKey)) next.delete(flatKey);
      else next.add(flatKey);
      return next;
    });
  };

  if (!groups.length) return null;

  if (totalGuests === 1) {
    const only = groups[0]?.units?.[0]?.guests?.[0]?.row;
    return (
      <div className="my-tickets-entry-qr-panel">
        <h2 className="my-tickets-entry-qr-title">
          {t("MY_TICKETS_ENTRY_QR_TITLE")}
        </h2>
        <div className="my-tickets-qr-card">
          <QrImage ticket={only} t={t} />
        </div>
      </div>
    );
  }

  return (
    <div className="my-tickets-entry-qr-panel">
      <h2 className="my-tickets-entry-qr-title">
        {t("MY_TICKETS_ENTRY_QR_TITLE")}
      </h2>

      {groups.map((cat) => {
        const catOpen = expandedCats.has(cat.categoryKey);
        const showTicketLevel = cat.units.length > 1;

        return (
          <div key={cat.categoryKey} className="my-tickets-entry-cat">
            <button
              type="button"
              className={`my-tickets-entry-cat-header${
                catOpen ? " my-tickets-entry-cat-header--open" : ""
              }`}
              onClick={() => toggleCat(cat.categoryKey)}
              aria-expanded={catOpen}
            >
              <span>
                {cat.categoryLabel} ({cat.totalPasses})
              </span>
              <i
                className={`bx ${catOpen ? "bx-chevron-up" : "bx-chevron-down"}`}
                aria-hidden
              />
            </button>

            <Collapse in={catOpen}>
              <div className="my-tickets-entry-cat-body">
                {cat.units.map((unit) => {
                  const unitId = `${cat.categoryKey}|${unit.unitKey}`;
                  const unitOpen =
                    !showTicketLevel || expandedUnits.has(unitId);

                  const guestList = (
                    <div className="my-tickets-entry-guest-list">
                      {unit.guests.map((guest) => {
                        const guestOpen = expandedGuests.has(guest.flatKey);
                        return (
                          <div key={guest.flatKey} className="my-tickets-entry-guest">
                            <button
                              type="button"
                              className={`my-tickets-entry-guest-header${
                                guestOpen
                                  ? " my-tickets-entry-guest-header--open"
                                  : ""
                              }`}
                              onClick={() => toggleGuest(guest.flatKey)}
                              aria-expanded={guestOpen}
                            >
                              <span>
                                {guestLabel(
                                  t,
                                  guest.guestIndex,
                                  guest.guestCount
                                )}
                              </span>
                              <i
                                className={`bx ${
                                  guestOpen
                                    ? "bx-chevron-up"
                                    : "bx-chevron-down"
                                }`}
                                aria-hidden
                              />
                            </button>
                            <Collapse in={guestOpen}>
                              <div className="my-tickets-entry-guest-qr">
                                <QrImage ticket={guest.row} t={t} />
                              </div>
                            </Collapse>
                          </div>
                        );
                      })}
                    </div>
                  );

                  if (!showTicketLevel) {
                    return (
                      <div key={unit.unitKey}>{guestList}</div>
                    );
                  }

                  return (
                    <div key={unit.unitKey} className="my-tickets-entry-unit">
                      <button
                        type="button"
                        className={`my-tickets-entry-unit-header${
                          unitOpen ? " my-tickets-entry-unit-header--open" : ""
                        }`}
                        onClick={() =>
                          toggleUnit(cat.categoryKey, unit.unitKey)
                        }
                        aria-expanded={unitOpen}
                      >
                        <span>
                          {t("MY_TICKETS_TICKET_UNIT_N", {
                            n: unit.unitIndex,
                          })}
                        </span>
                        <i
                          className={`bx ${
                            unitOpen ? "bx-chevron-up" : "bx-chevron-down"
                          }`}
                          aria-hidden
                        />
                      </button>
                      <Collapse in={unitOpen}>
                        <div className="my-tickets-entry-unit-body">
                          {guestList}
                        </div>
                      </Collapse>
                    </div>
                  );
                })}
              </div>
            </Collapse>
          </div>
        );
      })}

      <p className="my-tickets-entry-hint small mb-0">
        {t("MY_TICKETS_ENTRY_QR_HINT")}
      </p>
    </div>
  );
};

export default EntryQrAccordion;
